import { useEffect, useMemo, useRef, useState } from 'react'
import { pathFor } from '../routing.js'
import { ProtectionLock } from './lock.jsx'
import type { LockMark } from './lock.jsx'
import type { Fleet, FleetInstance } from '@qim/shared'
import type { TenantSummary } from '../api.js'
import type { JSX } from 'react'

/**
 * Type the name of the thing (IM-106).
 *
 * ## Why this earns its place
 *
 * With a fleet spanning customers, accounts and regions, the fastest navigation
 * is not a hierarchy — it is typing a fragment of what you are looking for. Two
 * screens deep is fine when there are four customers; it is not when there are
 * forty instances and the one you want is named in a ticket in front of you.
 *
 * ## Why it costs nothing
 *
 * It filters what the console already has. The fleet read carries every instance
 * with its customer, account and region, and `/tenants` carries the customer
 * list — so a keystroke fetches nothing. If this ever needs a round trip to
 * answer, the design has gone wrong.
 *
 * ## Why there is no scope check here
 *
 * Because there is nothing to check. The fleet read was already filtered by
 * `visibleTenants` server-side, so a scoped engineer's palette holds only their
 * customers by construction. A check here would be a second filter and a place
 * for the two to disagree — the same reason no other screen has one.
 */

interface Entry {
  readonly kind: 'instance' | 'customer' | 'account'
  readonly label: string
  readonly detail: string
  readonly path: string
  /** Everything worth matching against, lowercased once at build time. */
  readonly haystack: string
  /**
   * An instance's protection, so the lock beside its name here matches the
   * one on every other screen (IM-174). Already on the fleet read.
   */
  readonly lock?: {
    readonly mark: LockMark | undefined
    readonly kind: FleetInstance['tenantKind']
  }
}

const KIND_WORD: Readonly<Record<Entry['kind'], string>> = {
  instance: 'inst',
  customer: 'cust',
  account: 'acct',
}

const instanceEntry = (instance: FleetInstance): Entry => ({
  kind: 'instance',
  label: instance.deploymentId,
  lock: { mark: instance.protected, kind: instance.tenantKind },
  detail: [instance.tenantName, instance.region].filter(Boolean).join(' · '),
  path: pathFor({
    screen: 'deployment',
    tenantId: instance.tenantId,
    deploymentId: instance.deploymentId,
  }),
  haystack: (
    `${instance.deploymentId} ${instance.tenantName} ${instance.tenantId} ` +
    `${instance.accountKey} ${instance.region ?? ''}`
  ).toLowerCase(),
})

const customerEntry = (tenant: TenantSummary): Entry => ({
  kind: 'customer',
  label: tenant.name,
  detail: tenant.tenantId,
  path: pathFor({ screen: 'customer', tenantId: tenant.tenantId }),
  haystack: `${tenant.name} ${tenant.tenantId}`.toLowerCase(),
})

/**
 * One entry per account, from the instances that mention it.
 *
 * The fleet read is the only thing loaded on every screen, and it names accounts
 * without listing them — so accounts are derived from it rather than fetched.
 * An account with no instances is missing here, which is a real gap: since
 * IM-181 it is filled for the customer on screen by `openAccountEntries`, and
 * for any other customer reaching a brand-new account still goes through it.
 */
const accountEntries = (instances: readonly FleetInstance[]): readonly Entry[] => {
  const seen = new Map<string, Entry>()
  for (const instance of instances) {
    const key = `${instance.tenantId}/${instance.accountKey}`
    if (seen.has(key)) continue
    seen.set(key, {
      kind: 'account',
      label: instance.accountKey,
      detail: instance.tenantName,
      path: pathFor({
        screen: 'account',
        tenantId: instance.tenantId,
        accountKey: instance.accountKey,
      }),
      haystack: `${instance.accountKey} ${instance.tenantName} ${instance.tenantId}`.toLowerCase(),
    })
  }
  return [...seen.values()]
}

/**
 * The open customer's own accounts, when the screen behind the palette already
 * read them (IM-181).
 *
 * This closes the gap `accountEntries` names — a brand-new account has no
 * instances, and it is the account most likely to need its onboarding screen —
 * without a read of its own: the customer screen fetched this list to render
 * itself, and `App` hands it over only while it is on screen. It carries the
 * label, which the fleet does not, so an account listed both ways is kept
 * once, under its name.
 */
export interface OpenAccounts {
  readonly tenantId: string
  readonly accounts: readonly { readonly accountKey: string; readonly label: string }[]
}

const openAccountEntries = (open: OpenAccounts, tenants: readonly TenantSummary[]): Entry[] => {
  const tenantName = tenants.find((t) => t.tenantId === open.tenantId)?.name ?? open.tenantId
  return open.accounts.map((account) => ({
    kind: 'account',
    label: account.label,
    detail: `${account.accountKey} · ${tenantName}`,
    path: pathFor({ screen: 'account', tenantId: open.tenantId, accountKey: account.accountKey }),
    haystack: `${account.label} ${account.accountKey} ${tenantName} ${open.tenantId}`.toLowerCase(),
  }))
}

/** One entry per path, the first kept — so the labelled listing wins. */
const oncePerPath = (entries: readonly Entry[]): readonly Entry[] => {
  const seen = new Set<string>()
  return entries.filter((entry) => {
    if (seen.has(entry.path)) return false
    seen.add(entry.path)
    return true
  })
}

/** How many matches are offered. Beyond this, typing more is the better move. */
export const MAX_RESULTS = 8

export const Palette = ({
  fleet,
  tenants,
  accounts,
  onOpen,
  onClose,
}: {
  readonly fleet: Fleet | undefined
  readonly tenants: readonly TenantSummary[]
  /** The open customer's accounts, already read by its screen — see `OpenAccounts`. */
  readonly accounts?: OpenAccounts | undefined
  readonly onOpen: (path: string) => void
  readonly onClose: () => void
}): JSX.Element => {
  const dialog = useRef<HTMLDialogElement>(null)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    const element = dialog.current
    if (element === null) return
    element.showModal()
    return () => {
      if (element.open) element.close()
    }
  }, [])

  const entries = useMemo((): readonly Entry[] => {
    const instances = fleet?.instances ?? []
    return [
      ...instances.map(instanceEntry),
      ...tenants.map(customerEntry),
      ...oncePerPath([
        ...(accounts === undefined ? [] : openAccountEntries(accounts, tenants)),
        ...accountEntries(instances),
      ]),
    ]
  }, [fleet, tenants, accounts])

  const matches = useMemo((): readonly Entry[] => {
    const needle = query.trim().toLowerCase()
    if (needle === '') return entries.slice(0, MAX_RESULTS)
    return entries.filter((entry) => entry.haystack.includes(needle)).slice(0, MAX_RESULTS)
  }, [entries, query])

  // A filtered list is a different list, so the old position means nothing.
  useEffect(() => {
    setCursor(0)
  }, [query])

  const choose = (entry: Entry | undefined): void => {
    if (entry === undefined) return
    onOpen(entry.path)
    onClose()
  }

  return (
    <dialog
      className="palette"
      ref={dialog}
      aria-label="Jump to"
      /*
       * `cancel` is the dismissal signal, never `close` — the native `close`
       * event also fires for this component's own cleanup, which under
       * StrictMode's effect/cleanup/effect closes it the instant it opens. That
       * cost an afternoon in IM-79 and the note is repeated here because the
       * next person to add a dialog will reach for `onClose` first.
       */
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      /*
       * Clicking away closes it too, and this is the only hit area the dialog
       * itself has: the padding is zero, so every point inside it belongs to a
       * child, and a click reported against the element is a click on the
       * backdrop. Esc is the convention and the browser's own, but the palette
       * carries no visible close control, and reaching for the mouse when a
       * shortcut opened something by accident is exactly what people do.
       */
      onClick={(event) => {
        if (event.target === dialog.current) onClose()
      }}
    >
      <input
        className="palette-input"
        /* The usual objection to autofocus is that it steals focus from a page
          someone is reading. Here the dialog has just taken focus anyway, and
          this is the only thing in it worth having focus: a palette that has to
          be clicked before it can be typed into is slower than the navigation
          it replaces. */
        autoFocus
        value={query}
        placeholder="Jump to an instance, customer or account…"
        aria-label="Jump to"
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setCursor((at) => (matches.length === 0 ? 0 : (at + 1) % matches.length))
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setCursor((at) =>
              matches.length === 0 ? 0 : (at - 1 + matches.length) % matches.length,
            )
          }
          if (event.key === 'Enter') {
            event.preventDefault()
            choose(matches[cursor])
          }
        }}
      />

      {matches.length === 0 ? (
        <p className="palette-empty" role="status">
          Nothing matches “{query}”.
        </p>
      ) : (
        <ul className="palette-list">
          {matches.map((entry, index) => (
            <li key={`${entry.kind}:${entry.path}`}>
              <button
                type="button"
                className="palette-hit"
                /* The highlight is an attribute rather than a second class, so
                  the row someone is about to open is announced and not only
                  shaded — the same shape as `.tab[aria-current='true']`. */
                {...(index === cursor ? { 'aria-current': true as const } : {})}
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(entry)}
              >
                <span className="palette-kind">{KIND_WORD[entry.kind]}</span>
                <span className="palette-label">
                  {entry.label}
                  {entry.lock === undefined ? null : (
                    <ProtectionLock mark={entry.lock.mark} kind={entry.lock.kind} />
                  )}
                </span>
                <span className="palette-detail">{entry.detail}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </dialog>
  )
}
